// src/pages/Users/UserManagement.jsx - Add the edit limit button and modal with search
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/userService';
import adminService from '../../services/adminService';
import { toast } from 'react-toastify';
import CreateUserModal from './CreateUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import EditCommissionModal from './EditCommissionModal';
import EditRetailerLimitModal from '../../components/EditRetailerLimitModal';

const UserManagement = ({ role, title }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm]);

  // Define fetchUsers with useCallback
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getUsersByRole(
        role, 
        pagination.page, 
        pagination.limit,
        debouncedSearchTerm
      );
      setUsers(response.users || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 1
      }));
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [role, pagination.page, pagination.limit, debouncedSearchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (userData) => {
    try {
      // userData already contains commissionType for distributors/admins
      const response = await userService.createUser({ ...userData, role });
      toast.success(`${title} created successfully`);
      setShowCreateModal(false);
      fetchUsers();
      return response; // Return response to access generatedUsername
    } catch (error) {
      if (error.response?.data?.error?.includes('User creation limit exceeded')) {
        toast.error('User creation limit exceeded. Please contact admin to increase your limit.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to create user');
      }
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleChangePassword = async (userId, newPassword) => {
    try {
      await userService.changePassword(userId, newPassword);
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    }
  };

  const handleEditCommission = async (userId, newCommissionRate, reason) => {
    try {
      await userService.updateCommission(userId, newCommissionRate, reason);
      toast.success('Commission rate updated successfully');
      setShowCommissionModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update commission');
    }
  };

  const handleEditLimit = async (userId, newLimit) => {
    try {
      await adminService.updateRetailerLimit(userId, newLimit);
      toast.success('User creation limit updated successfully');
      setShowLimitModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update limit');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await userService.toggleStatus(userId);
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete ${username}?`)) {
      try {
        await userService.deleteUser(userId);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const openCommissionModal = (user) => {
    setSelectedUser(user);
    setShowCommissionModal(true);
  };

  const openLimitModal = (user) => {
    setSelectedUser(user);
    setShowLimitModal(true);
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'admin':
        return 'badge-admin';
      case 'sub-admin':
        return 'badge-subadmin';
      case 'distributor':
        return 'badge-distributor';
      case 'retailer':
        return 'badge-retailer';
      default:
        return 'badge-user';
    }
  };

  const getRoleDisplay = (role) => {
    switch(role) {
      case 'admin':
        return 'Super Admin';
      case 'sub-admin':
        return 'Sub Admin';
      case 'distributor':
        return 'Distributor';
      case 'retailer':
        return 'Retailer';
      default:
        return role;
    }
  };



  return (
    <div className="list-of-page">
      <div className="container-fluid py-4">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">List Of {title}</h1>
          {(currentUser?.role === 'admin' || currentUser?.role === 'sub-admin' || currentUser?.role === 'distributor' || currentUser?.role === 'retailer') && (
            <button className="new-user-add" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus"></i> Add New {title}
            </button>
          )}
        </div>
        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">{title}</h2>
            {/* Search Input */}
            <div className="search-container" style={{ marginTop: '15px' }}>
              <div className="input-group" style={{ maxWidth: '300px', marginLeft: 'auto' }}>
                <span className="input-group-text bg-transparent text-white border-secondary">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control bg-dark text-white border-secondary"
                  placeholder="Search by username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearchTerm('')}
                    style={{ color: '#fff' }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="admin-card-body">
            <div className="table-responsive">
              <table className="table table-admin-one">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Parent Name</th>
                    <th>Name/ID</th>
                    <th>Percentage</th>
                    <th>Balance</th>
                    <th>Status</th>
                    {role === 'retailer' && <th>User Limit</th>}
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
  {loading ? (
    <tr>
      <td colSpan={role === 'retailer' ? 8 : 7} className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </td>
    </tr>
  ) : users.length > 0 ? (
    users.map((user) => (
      <tr key={user._id}>
        <td>{user.username}</td>
        <td>
          {user?.parentId?.username || '-'} ({user?.parentId?.fullName || '-'})
        </td>
        <td>
          {user?.fullName || '-'}
        </td>
        <td>
          <span className="commission-rate">
            {user.commissionRate || 0}% / {user.commissionType}
          </span>
        </td>
        <td>
          ₹
          {user.walletBalance?.toFixed(2) ??
            user.wallet_balance?.toFixed(2) ??
            '0.00'}
        </td>
        <td>
          <span
            className={`badge ${
              user.isActive ? 'badge-active' : 'badge-inactive'
            }`}
          >
            {user.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>

        {role === 'retailer' && (
          <td>
            <div className="d-flex flex-column">
              <span className="badge bg-info mb-1">
                {user.usersCreatedCount || 0} / {user.userCreationLimit || 10}
              </span>
              <div className="progress" style={{ height: '5px' }}>
                <div
                  className={`progress-bar ${
                    (user.usersCreatedCount || 0) >=
                    (user.userCreationLimit || 10)
                      ? 'bg-danger'
                      : 'bg-success'
                  }`}
                  style={{
                    width: `${
                      ((user.usersCreatedCount || 0) /
                        (user.userCreationLimit || 10)) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </td>
        )}

        <td>
          <div className="d-flex justify-content-center gap-2">
            <button
              className="btn-edit"
              onClick={() => openPasswordModal(user)}
            >
              <i className="fas fa-key"></i>
            </button>

            {(user.role !== 'admin' && user.role !== 'user') && (
              <button
                className="btn-commission"
                onClick={() => openCommissionModal(user)}
              >
                <i className="fas fa-percentage"></i>
              </button>
            )}

            {user.role === 'retailer' &&
              currentUser?.role === 'admin' && (
                <button
                  className="btn-limit"
                  onClick={() => openLimitModal(user)}
                >
                  <i className="fas fa-chart-line"></i>
                </button>
              )}

            <button
              className={`btn-lock ${
                !user.isActive ? 'locked' : ''
              }`}
              onClick={() => handleToggleStatus(user._id)}
            >
              <i
                className={`fas ${
                  user.isActive ? 'fa-unlock-alt' : 'fa-lock'
                }`}
              ></i>
            </button>

            {(currentUser?.role === 'admin' ||
              currentUser?.role === 'sub-admin') && (
              <button
                className="btn-delete"
                onClick={() =>
                  handleDeleteUser(user._id, user.username)
                }
              >
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td
        colSpan={role === 'retailer' ? 8 : 7}
        className="text-center py-4"
      >
        {searchTerm
          ? `No ${title.toLowerCase()} found matching "${searchTerm}"`
          : `No ${title.toLowerCase()} found`}
      </td>
    </tr>
  )}
</tbody>
              </table>
            </div>
            
            {pagination.pages > 1 && (
              <div className="admin-table-bottom">
                <div className="table-info">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} entries
                </div>
                <div className="table-pagination">
                  <button 
                    className="page-btn prev"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Prev
                  </button>
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button 
                    className="page-btn next"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateUser}
        role={role}
        title={title}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleChangePassword}
        user={selectedUser}
      />

      {/* Edit Commission Modal */}
      <EditCommissionModal
        show={showCommissionModal}
        onClose={() => {
          setShowCommissionModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleEditCommission}
        user={selectedUser}
      />

      {/* Edit Retailer Limit Modal */}
      <EditRetailerLimitModal
        show={showLimitModal}
        onClose={() => {
          setShowLimitModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleEditLimit}
        user={selectedUser}
      />
    </div>
  );
};

export default UserManagement;